import {
  completeWithdrawalSync,
  getWithdrawalSyncStatus,
  listWithdrawalServerFiles,
  syncWithdrawalMetadata,
  syncWithdrawalVersion,
  uploadWithdrawalFileRaw,
} from "../api/sync";

import {
  acquireLocalWithdrawalSyncLease,
  compareAndSwapLocalWithdrawal,
  createLocalSyncLeaseOwner,
  deleteRecoverySnapshot,
  getAllLocalWithdrawals,
  getAllRecoverySnapshots,
  getRecoverySnapshot,
  getLocalWithdrawal,
  getPendingLocalWithdrawals,
  LocalWithdrawalRevisionConflictError,
  releaseLocalWithdrawalSyncLease,
  renewLocalWithdrawalSyncLease,
  saveLocalWithdrawal,
  saveRecoverySnapshot,
  updateLocalWithdrawalAtomically,
} from "../storage/offlineDatabase";

import type { LocalSyncPhase, LocalWithdrawalRecord } from "../types/offlineWithdrawal";
import { getLastKnownAuthUser } from "./authService";
import { rememberConfirmedWithdrawalNumber } from "./withdrawalIdentityService";
import { registerMaterialentnahmeBackgroundSync } from "./serviceWorkerService";
import { syncLocalDraft } from "./draftService";
import { fetchIncompleteServerSyncs, matchLocalRecoveryRecord } from "./syncRecoveryService";
import { WITHDRAWAL_DOCUMENT_ARTIFACT_VERSION } from "./withdrawalDocumentConstants";
import { classifyRequestFailure, fetchWithTimeout, HttpError } from "../utils/fetchWithTimeout";
import { isActiveDiagnosticSimulationForUuid, isDiagnosticEmailTest, maybeThrowSimulatedSyncFailure } from "./syncFailureSimulationService";
import { stampScanDebugImageIdentity } from "./scanDebugImageService";
import { verifyServerArchiveAndCompact } from "./localStorageLifecycleService";


let syncRunning = false;
const activeWithdrawalSyncs = new Map<string, Promise<void>>();

async function softDeleteServerWithdrawal(withdrawalId: number): Promise<void> {
  const response = await fetch(`/api/withdrawals/${withdrawalId}`, { method: "DELETE", credentials: "include" });
  if (!response.ok) throw new HttpError(response.status, `Löschstatus konnte nicht mit dem Server abgeglichen werden (${response.status}).`);
}



async function refreshDiagnosticIdentity(
  record: LocalWithdrawalRecord
): Promise<LocalWithdrawalRecord> {
  if (!record.server_withdrawal_id) return record;
  const identity = {
    withdrawalNumber: record.local_withdrawal_number ?? record.server_withdrawal_number ?? null,
    serverId: record.server_withdrawal_id,
    clientUuid: record.client_uuid,
  };
  let debugDataBlob = record.debug_data_blob ?? null;
  let debugImageBlob = record.debug_image_blob ?? null;
  try {
    if (debugDataBlob) {
      const parsed = JSON.parse(await debugDataBlob.text()) as Record<string, unknown>;
      parsed.identity = identity;
      const scanDetails = parsed.scan_details;
      if (scanDetails && typeof scanDetails === "object") {
        (scanDetails as Record<string, unknown>).identity = identity;
      }
      parsed.identity_updated_at = new Date().toISOString();
      debugDataBlob = new Blob([JSON.stringify(parsed, null, 2)], { type: "application/json" });
    }
  } catch {
    // Diagnose-Metadaten bleiben best effort.
  }
  try {
    if (debugImageBlob) {
      debugImageBlob = await stampScanDebugImageIdentity(debugImageBlob, identity);
    }
  } catch {
    // Debug-Bild darf den fachlichen Sync niemals blockieren.
  }
  if (debugDataBlob === record.debug_data_blob && debugImageBlob === record.debug_image_blob) return record;
  return updateRecord(record, {
    debug_data_blob: debugDataBlob,
    debug_image_blob: debugImageBlob,
    updated_at: new Date().toISOString(),
  });
}

const SYNC_RETRY_BASE_DELAY_MS = 30_000;
const SYNC_RETRY_MAX_DELAY_MS = 15 * 60_000;
const MAX_SYNC_ATTEMPTS = 9;
const SYNC_LEASE_HEARTBEAT_MS = 60_000;

function syncRetryDelayMs(attempt: number): number {
  const exponent = Math.max(0, Math.min(5, attempt - 1));
  const base = Math.min(SYNC_RETRY_MAX_DELAY_MS, SYNC_RETRY_BASE_DELAY_MS * (2 ** exponent));
  const jitter = 0.85 + Math.random() * 0.30;
  return Math.min(SYNC_RETRY_MAX_DELAY_MS, Math.round(base * jitter));
}

function classifySyncError(error: unknown): "connectivity" | "conflict" | "terminal" | "retry" {
  const requestFailure = classifyRequestFailure(error);
  if (requestFailure === "timeout" || requestFailure === "network" || requestFailure === "aborted") return "connectivity";
  if (error instanceof HttpError) {
    if (error.isConflict) return "conflict";
    if (error.isValidation || error.isAuth) return "terminal";
    if (error.isRetryable) return "retry";
  }
  const message = error instanceof Error ? error.message : String(error ?? "");
  if (isConnectivitySyncError(message)) return "connectivity";
  if (isStructuralLocalSyncError(message)) return "terminal";
  return "retry";
}


async function updateRecord(
  record:
    LocalWithdrawalRecord,
  changes:
    Partial<LocalWithdrawalRecord>
): Promise<LocalWithdrawalRecord> {
  const sourceRevision = Math.max(0, record.content_revision ?? 0);
  const targetRevision = changes.content_revision === undefined
    ? sourceRevision
    : Math.max(0, changes.content_revision ?? 0);

  // Fachliche/Artefaktänderungen, die content_revision erhöhen, benötigen
  // denselben echten IDB-CAS wie normale Window-Writes. Eine inzwischen neuere
  // Revision gewinnt und wird niemals durch einen älteren Sync-Self-Heal ersetzt.
  if (targetRevision !== sourceRevision) {
    try {
      const guarded = await compareAndSwapLocalWithdrawal(record.client_uuid, sourceRevision, (current) => {
        if (!current) return null;
        return {
          ...current,
          ...changes,
          updated_at: new Date().toISOString(),
        };
      });
      return guarded ?? record;
    } catch (error) {
      if (error instanceof LocalWithdrawalRevisionConflictError) {
        return await getLocalWithdrawal(record.client_uuid).catch(() => null) ?? record;
      }
      throw error;
    }
  }

  // Reine Sync-Metadaten werden ebenfalls in genau einer readwrite-Transaktion
  // auf den aktuellsten Datensatz gemerged. Sie dürfen content_revision nicht
  // verändern und keine parallel gespeicherten Formulardaten überschreiben.
  const updated = await updateLocalWithdrawalAtomically(record.client_uuid, (current) => ({
    ...current,
    ...changes,
    content_revision: current.content_revision,
    updated_at: new Date().toISOString(),
  }));
  return updated ?? record;
}

const MAX_SYNC_TRACE_ENTRIES = 48;

async function markSyncPhase(
  record: LocalWithdrawalRecord,
  phase: LocalSyncPhase,
  state: "started" | "completed" | "failed",
  detail?: string | null,
): Promise<LocalWithdrawalRecord> {
  const latest = await getLocalWithdrawal(record.client_uuid).catch(() => null);
  const base = latest ?? record;
  const at = new Date().toISOString();
  const trace = [
    ...(base.sync_trace ?? []),
    { phase, state, at, detail: detail ?? null },
  ].slice(-MAX_SYNC_TRACE_ENTRIES);

  const changes: Partial<LocalWithdrawalRecord> = {
    sync_phase: phase,
    sync_phase_updated_at: at,
    sync_trace: trace,
  };

  if (state === "started" && phase === "backend_check") {
    changes.sync_failure_phase = null;
    changes.sync_failure_detail = null;
  } else if (state === "completed") {
    changes.last_successful_sync_phase = phase;
  } else if (state === "failed") {
    changes.sync_failure_phase = phase;
    changes.sync_failure_detail = detail ?? "Unbekannter Synchronisierungsfehler.";
  }

  return updateRecord(base, changes);
}


function isStructuralLocalSyncError(message: string): boolean {
  return /lokale .*datei fehlt|lokale .*daten fehlen|gehört zu einem anderen benutzer|vorgang nicht gefunden|vorlage konnte nicht geladen|dokument.*konnte nicht erzeugt|notwendige entnahmedaten fehlen/i.test(message);
}

function isConnectivitySyncError(message: string): boolean {
  return /server nicht erreichbar|serververbindung.*nicht verfügbar|serverantwort.*zu langsam|server ist erreichbar.*fehler|statusprüfung.*fehler|zeitüberschreitung|timeout|failed to fetch|load failed|networkerror|network request failed|aborterror/i.test(message);
}

function documentDataFromRecord(record: LocalWithdrawalRecord) {
  const data = record.data;
  return {
    withdrawalDate: data.withdrawal_date, materialNumber: data.material_number, materialText: data.material_text, materialType: data.material_type ?? "",
    storageAddress: [data.building, data.floor, data.cabinet, data.compartment, data.sub_compartment].filter(Boolean).join(" "),
    quantityRemoved: data.quantity_removed, currentStock: data.current_stock, persons: data.persons.map((person) => person.name),
    accountingNumber: data.accounting_number ?? "", accountingDescription: [data.accounting_system, data.accounting_subarea].filter(Boolean).join(" - "), orderInfo: data.order_info ?? "",
  };
}

function assertRequiredDocumentData(record: LocalWithdrawalRecord): void {
  // Die Vorlage unterstützt bewusst leere fachliche Felder. Dadurch besitzt auch
  // ein bereits in Schritt 2 gespeicherter Entwurf immer einen Materialentnahmeschein.
  // Nur ein Entnahmedatum ist für die Dokumentidentität zwingend erforderlich.
  if (!record.data.withdrawal_date) {
    throw new Error("Entnahmedatum fehlt. Der Materialentnahmeschein kann lokal nicht neu erzeugt werden.");
  }
}

async function regenerateRequiredDocuments(record: LocalWithdrawalRecord, reason: string): Promise<LocalWithdrawalRecord> {
  assertRequiredDocumentData(record);
  const { createWithdrawalDocumentArtifacts } = await import("./withdrawalDocumentService");
  const artifacts = await createWithdrawalDocumentArtifacts(documentDataFromRecord(record));
  const base = `#${record.local_withdrawal_number ?? record.server_withdrawal_number ?? "–"}_${record.data.material_number}_${record.data.withdrawal_date}`;
  const regenerated = await updateRecord(record, {
    pdf_blob: artifacts.pdf,
    pdf_filename: record.pdf_filename ?? `${base}.pdf`,
    pdf_preview_blob: artifacts.preview,
    pdf_preview_filename: record.pdf_preview_filename ?? `${base}_Vorschau.webp`,
    pdf_blob_health: "regenerated",
    pdf_blob_health_detail: `Neu erzeugt · ${artifacts.pdf.size} Byte`,
    preview_blob_health: "regenerated",
    preview_blob_health_detail: `Neu erzeugt · ${artifacts.preview.size} Byte`,
    document_self_heal_at: new Date().toISOString(),
    document_self_heal_reason: reason,
    document_self_heal_count: (record.document_self_heal_count ?? 0) + 1,
    document_artifact_version: WITHDRAWAL_DOCUMENT_ARTIFACT_VERSION,
    document_server_refresh_required: true,
    content_revision: Math.max(0, record.content_revision ?? 0) + 1,
    sync_status: "pending",
    synced_at: null,
    next_sync_attempt_at: null,
    last_error: null,
  });
  // Die frischen Bytes werden im zentralen Recovery-Schritt genau einmal
  // persistiert. Dadurch werden große Safari/iOS-Blobs nicht mehrfach direkt
  // hintereinander durch IndexedDB kopiert.
  return regenerated;
}

async function ensureRequiredDocuments(record: LocalWithdrawalRecord): Promise<LocalWithdrawalRecord> {
  const complete = Boolean(record.pdf_blob && record.pdf_filename && record.pdf_preview_blob && record.pdf_preview_filename);
  if (complete && record.document_artifact_version === WITHDRAWAL_DOCUMENT_ARTIFACT_VERSION) return record;
  assertRequiredDocumentData(record);
  // Vor v0.116.2 konnten transparente/leere Canvas-Artefakte technisch lesbar sein
  // und deshalb die bisherige Blob-Prüfung passieren. Jede ältere/ungekennzeichnete
  // Dokumentgeneration wird einmal aus den fachlichen Daten neu aufgebaut.
  return await regenerateRequiredDocuments(
    record,
    complete ? `Dokumentgeneration ${record.document_artifact_version ?? "legacy/unbekannt"} wird auf ${WITHDRAWAL_DOCUMENT_ARTIFACT_VERSION} aktualisiert.` : "Pflichtdokument fehlt.",
  );
}

function blobReadErrorText(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

async function verifyRequiredDocumentBlobs(record: LocalWithdrawalRecord): Promise<LocalWithdrawalRecord> {
  let current = record;
  const pdf = current.pdf_blob;
  const preview = current.pdf_preview_blob;
  let pdfError: string | null = null;
  let previewError: string | null = null;

  if (pdf) {
    try {
      await pdf.arrayBuffer();
      current = await updateRecord(current, { pdf_blob_health: "readable", pdf_blob_health_detail: `Bytes lesbar · ${pdf.size} Byte` });
    } catch (error) {
      pdfError = blobReadErrorText(error);
      current = await updateRecord(current, { pdf_blob_health: "unreadable", pdf_blob_health_detail: pdfError });
    }
  }

  if (preview) {
    try {
      await preview.arrayBuffer();
      current = await updateRecord(current, { preview_blob_health: "readable", preview_blob_health_detail: `Bytes lesbar · ${preview.size} Byte` });
    } catch (error) {
      previewError = blobReadErrorText(error);
      current = await updateRecord(current, { preview_blob_health: "unreadable", preview_blob_health_detail: previewError });
    }
  }

  if (pdfError || previewError) {
    const reason = [pdfError ? `PDF nicht lesbar: ${pdfError}` : null, previewError ? `Vorschau nicht lesbar: ${previewError}` : null].filter(Boolean).join(" · ");
    current = await markSyncPhase(current, "document_self_heal", "started", `${reason} · Pflichtdokumente werden aus den Entnahmedaten neu erzeugt.`);
    current = await regenerateRequiredDocuments(current, reason);
    // Frisch erzeugte In-Memory-Blobs müssen lesbar sein. Falls nicht, ist dies ein
    // echter struktureller Fehler und darf nicht neunmal wiederholt werden.
    try {
      await current.pdf_blob?.arrayBuffer();
      await current.pdf_preview_blob?.arrayBuffer();
    } catch (error) {
      const detail = `Neu erzeugte Dokumente sind nicht lesbar: ${blobReadErrorText(error)}`;
      await markSyncPhase(current, "document_self_heal", "failed", detail);
      throw new Error(detail, { cause: error });
    }
    current = await markSyncPhase(current, "document_self_heal", "completed", `Self-Healing erfolgreich · PDF ${current.pdf_blob?.size ?? 0} Byte · Vorschau ${current.pdf_preview_blob?.size ?? 0} Byte.`);
  }

  return current;
}

export async function restoreLocalRequiredDocumentsByUuid(clientUuid: string): Promise<LocalWithdrawalRecord> {
  const [main, snapshots] = await Promise.all([getLocalWithdrawal(clientUuid), getAllRecoverySnapshots()]);
  const recovery = snapshots.find((item) => item.client_uuid === clientUuid) ?? null;
  const source = main ?? recovery;
  if (!source) throw new Error("Lokaler Vorgang nicht gefunden.");

  // Fehlende Artefakte immer in den aktuellsten IndexedDB-Stand einfügen.
  // Anders als der frühere Full-Record-Merge überschreibt dieser Pfad niemals
  // parallel geänderte Formulardaten, Synczustände oder content_revision.
  const latest = await getLocalWithdrawal(clientUuid).catch(() => null);
  let prepared = latest ?? source;
  const recoveryArtifacts: Partial<LocalWithdrawalRecord> = {};
  const recover = <K extends keyof LocalWithdrawalRecord>(key: K): void => {
    if (prepared[key] == null && recovery?.[key] != null) recoveryArtifacts[key] = recovery[key] as LocalWithdrawalRecord[K];
  };
  recover("pdf_blob");
  recover("pdf_filename");
  recover("pdf_preview_blob");
  recover("pdf_preview_filename");
  recover("label_blob");
  recover("label_filename");
  recover("source_blob");
  recover("source_filename");
  recover("detection_source_blob");
  recover("detection_source_filename");
  recover("analysis_input_blob");
  recover("analysis_input_filename");
  recover("rectification_input_blob");
  recover("rectification_input_filename");
  recover("ocr_warp_blob");
  recover("ocr_warp_filename");
  recover("vision_feedback_blob");
  recover("vision_feedback_filename");
  recover("live_training_blob");
  recover("live_training_filename");
  recover("debug_image_blob");
  recover("debug_image_filename");
  recover("debug_data_blob");
  recover("debug_data_filename");

  if (!latest || Object.keys(recoveryArtifacts).length > 0) {
    prepared = { ...prepared, ...recoveryArtifacts };
    await saveLocalWithdrawal(prepared);
  }

  const draftSavedAt = prepared.record_type === "draft" ? prepared.updated_at : null;
  const draftRevisionBeforeRepair = prepared.record_type === "draft" ? (prepared.content_revision ?? 0) : null;
  prepared = await ensureRequiredDocuments(prepared);

  // Eine rein technische Dokument-Selbstheilung ist kein erneutes fachliches
  // Speichern des Entwurfs. Der sichtbare Zeitstempel „Zuletzt gespeichert“
  // bleibt deshalb erhalten. Bei einer parallel neu gespeicherten Revision wird
  // bewusst nichts zurückgeschrieben.
  if (draftSavedAt && draftRevisionBeforeRepair !== null && (prepared.content_revision ?? 0) > draftRevisionBeforeRepair) {
    const newest = await getLocalWithdrawal(clientUuid).catch(() => null);
    if (newest && (newest.content_revision ?? 0) === (prepared.content_revision ?? 0)) {
      prepared = { ...newest, updated_at: draftSavedAt };
      await saveLocalWithdrawal(prepared);
    }
  }

  await saveRecoverySnapshot(prepared);
  if (prepared.sync_status === "pending") void registerMaterialentnahmeBackgroundSync();
  return prepared;
}


const RECOVERY_OPERATION_TIMEOUT_MS = 2_500;

async function withLocalOperationTimeout<T>(promise: Promise<T>, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timer = setTimeout(() => reject(new Error(`${label} hat nach ${RECOVERY_OPERATION_TIMEOUT_MS} ms nicht geantwortet.`)), RECOVERY_OPERATION_TIMEOUT_MS);
      }),
    ]);
  } finally {
    if (timer !== null) clearTimeout(timer);
  }
}

function sameRecoveryPayload(a: LocalWithdrawalRecord, b: LocalWithdrawalRecord): boolean {
  const blobMeta = (blob: Blob | null | undefined, filename: string | null | undefined) => ({
    filename: filename ?? null,
    size: blob?.size ?? 0,
    type: blob?.type ?? "",
    present: Boolean(blob),
  });
  return JSON.stringify({
    owner_user_id: a.owner_user_id,
    owner_username: a.owner_username,
    local_withdrawal_number: a.local_withdrawal_number,
    server_withdrawal_id: a.server_withdrawal_id,
    server_withdrawal_number: a.server_withdrawal_number,
    base_version: a.base_version,
    record_type: a.record_type,
    deleted_at: a.deleted_at ?? null,
    data: a.data,
    pdf: blobMeta(a.pdf_blob, a.pdf_filename),
    preview: blobMeta(a.pdf_preview_blob, a.pdf_preview_filename),
    label: blobMeta(a.label_blob, a.label_filename),
    rectificationInput: blobMeta(a.rectification_input_blob, a.rectification_input_filename),
    ocrWarp: blobMeta(a.ocr_warp_blob, a.ocr_warp_filename),
    visionFeedback: blobMeta(a.vision_feedback_blob, a.vision_feedback_filename),
    liveTraining: blobMeta(a.live_training_blob, a.live_training_filename),
    debugImage: blobMeta(a.debug_image_blob, a.debug_image_filename),
    debugData: blobMeta(a.debug_data_blob, a.debug_data_filename),
    document_self_heal_at: a.document_self_heal_at ?? null,
  }) === JSON.stringify({
    owner_user_id: b.owner_user_id,
    owner_username: b.owner_username,
    local_withdrawal_number: b.local_withdrawal_number,
    server_withdrawal_id: b.server_withdrawal_id,
    server_withdrawal_number: b.server_withdrawal_number,
    base_version: b.base_version,
    record_type: b.record_type,
    deleted_at: b.deleted_at ?? null,
    data: b.data,
    pdf: blobMeta(b.pdf_blob, b.pdf_filename),
    preview: blobMeta(b.pdf_preview_blob, b.pdf_preview_filename),
    label: blobMeta(b.label_blob, b.label_filename),
    rectificationInput: blobMeta(b.rectification_input_blob, b.rectification_input_filename),
    ocrWarp: blobMeta(b.ocr_warp_blob, b.ocr_warp_filename),
    visionFeedback: blobMeta(b.vision_feedback_blob, b.vision_feedback_filename),
    liveTraining: blobMeta(b.live_training_blob, b.live_training_filename),
    debugImage: blobMeta(b.debug_image_blob, b.debug_image_filename),
    debugData: blobMeta(b.debug_data_blob, b.debug_data_filename),
    document_self_heal_at: b.document_self_heal_at ?? null,
  });
}

async function persistRecoverySnapshotForSync(record: LocalWithdrawalRecord): Promise<LocalWithdrawalRecord> {
  const startedAt = performance.now();
  record = await markSyncPhase(record, "recovery_prepare", "started", "Recovery-Persistierung wird vorbereitet.");
  record = await markSyncPhase(record, "recovery_lookup", "started", "Vorhandene Recovery-Schutzkopie wird gelesen.");
  let existing: LocalWithdrawalRecord | null;
  try {
    existing = await withLocalOperationTimeout(getRecoverySnapshot(record.client_uuid), "Recovery-Lesen");
    record = await markSyncPhase(record, "recovery_lookup", "completed", existing ? "Vorhandene Recovery-Schutzkopie gefunden." : "Noch keine Recovery-Schutzkopie vorhanden.");
  } catch (error) {
    const detail = `Recovery-Schutzkopie konnte nicht gelesen werden: ${blobReadErrorText(error)}`;
    record = await updateRecord(record, {
      recovery_snapshot_last_action: "failed",
      recovery_snapshot_last_at: new Date().toISOString(),
      recovery_snapshot_detail: detail,
    });
    record = await markSyncPhase(record, "recovery_lookup", "completed", `WARNUNG · ${detail}`);
    // Recovery ist eine zusätzliche Schutzebene. Ein Lesefehler darf einen sonst
    // vollständig synchronisierbaren Vorgang nicht dauerhaft blockieren.
    return record;
  }

  if (existing && sameRecoveryPayload(record, existing)) {
    const duration = Math.round(performance.now() - startedAt);
    record = await updateRecord(record, {
      recovery_snapshot_last_action: "skipped",
      recovery_snapshot_last_duration_ms: duration,
      recovery_snapshot_last_at: new Date().toISOString(),
      recovery_snapshot_detail: "Vorhandene Recovery-Schutzkopie ist bereits aktuell; erneutes Schreiben übersprungen.",
    });
    record = await markSyncPhase(record, "recovery_prepare", "completed", `Recovery bereits aktuell · Schreiben übersprungen · ${duration} ms.`);
    return record;
  }

  record = await markSyncPhase(record, "recovery_write", "started", existing ? "Recovery-Schutzkopie wird mit geänderten Daten aktualisiert." : "Recovery-Schutzkopie wird erstmalig gespeichert.");
  const writeStartedAt = performance.now();
  try {
    maybeThrowSimulatedSyncFailure(record, "recovery_write");
    await withLocalOperationTimeout(saveRecoverySnapshot(record), "Recovery-Schreiben");
  } catch (error) {
    const duration = Math.round(performance.now() - startedAt);
    const detail = `Recovery-Schutzkopie konnte nicht gespeichert werden: ${blobReadErrorText(error)}`;
    record = await updateRecord(record, {
      recovery_snapshot_last_action: "failed",
      recovery_snapshot_last_duration_ms: duration,
      recovery_snapshot_last_at: new Date().toISOString(),
      recovery_snapshot_detail: detail,
    });
    record = await markSyncPhase(record, "recovery_write", "completed", `WARNUNG · ${detail} · ${Math.round(performance.now() - writeStartedAt)} ms.`);
    // Auch ein Recovery-Schreibfehler ist kein Grund, den eigentlichen Server-Sync
    // zu verhindern: Hauptdatensatz und frische In-Memory-Dokumente bleiben erhalten.
    return record;
  }
  record = await markSyncPhase(record, "recovery_write", "completed", `Recovery-Schreibvorgang abgeschlossen · ${Math.round(performance.now() - writeStartedAt)} ms.`);

  record = await markSyncPhase(record, "recovery_verify", "started", "Gespeicherte Recovery-Schutzkopie wird verifiziert.");
  try {
    const verified = await withLocalOperationTimeout(getRecoverySnapshot(record.client_uuid), "Recovery-Verifikation");
    const valid = Boolean(verified && sameRecoveryPayload(record, verified));
    const duration = Math.round(performance.now() - startedAt);
    if (!valid) {
      const detail = "Recovery-Verifikation ergab keinen vollständig passenden Snapshot.";
      record = await updateRecord(record, {
        recovery_snapshot_last_action: "failed",
        recovery_snapshot_last_duration_ms: duration,
        recovery_snapshot_last_at: new Date().toISOString(),
        recovery_snapshot_detail: detail,
      });
      record = await markSyncPhase(record, "recovery_verify", "completed", `WARNUNG · ${detail} · ${duration} ms.`);
      return record;
    }
    record = await updateRecord(record, {
      recovery_snapshot_last_action: "written",
      recovery_snapshot_last_duration_ms: duration,
      recovery_snapshot_last_at: new Date().toISOString(),
      recovery_snapshot_detail: "Recovery-Schutzkopie gespeichert und verifiziert.",
    });
    record = await markSyncPhase(record, "recovery_verify", "completed", `Recovery gespeichert und verifiziert · Gesamt ${duration} ms.`);
    record = await markSyncPhase(record, "recovery_prepare", "completed", `Recovery-Persistierung abgeschlossen · ${duration} ms.`);
    return record;
  } catch (error) {
    const duration = Math.round(performance.now() - startedAt);
    const detail = `Recovery-Verifikation fehlgeschlagen: ${blobReadErrorText(error)}`;
    record = await updateRecord(record, {
      recovery_snapshot_last_action: "failed",
      recovery_snapshot_last_duration_ms: duration,
      recovery_snapshot_last_at: new Date().toISOString(),
      recovery_snapshot_detail: detail,
    });
    record = await markSyncPhase(record, "recovery_verify", "completed", `WARNUNG · ${detail} · ${duration} ms.`);
    return record;
  }
}

type BackendSyncAvailability = "ok" | "slow" | "offline" | "http_error";

async function backendAvailabilityForSync(): Promise<BackendSyncAvailability> {
  try {
    const response = await fetchWithTimeout("/api/status", { credentials: "include", cache: "no-store" }, 1200);
    // Jede HTTP-Antwort beweist Erreichbarkeit. Ein 5xx ist ein Serverfehler,
    // aber ausdrücklich kein Offline-Zustand.
    return response.ok ? "ok" : "http_error";
  } catch (error) {
    return classifyRequestFailure(error) === "timeout" ? "slow" : "offline";
  }
}

function backendAvailabilityMessage(state: BackendSyncAvailability): string {
  if (state === "slow") return "Serverantwort derzeit zu langsam. Die Synchronisierung wird automatisch fortgesetzt, sobald eine rechtzeitige Antwort möglich ist.";
  if (state === "http_error") return "Der Server ist erreichbar, hat die Statusprüfung aber mit einem Fehler beantwortet. Die Synchronisierung wird später erneut versucht.";
  return "Serververbindung derzeit nicht verfügbar. Die Synchronisierung wird fortgesetzt, sobald die Verbindung wieder verfügbar ist.";
}

async function assertBackendAvailableForManualRetry(): Promise<void> {
  const state = await backendAvailabilityForSync();
  if (state !== "ok") {
    if (state === "slow") throw new Error("Synchronisierung konnte nicht erneut gestartet werden. Der Server antwortet derzeit zu langsam; versuche es in Kürze erneut.");
    if (state === "http_error") throw new Error("Synchronisierung konnte nicht erneut gestartet werden. Der Server ist erreichbar, meldet aber derzeit einen Fehler.");
    throw new Error("Synchronisierung konnte nicht erneut gestartet werden. Die Serververbindung ist derzeit nicht verfügbar.");
  }
}


async function syncSingleWithdrawalUnlocked(
  initialRecord:
    LocalWithdrawalRecord
): Promise<void> {
  let record = (await getLocalWithdrawal(initialRecord.client_uuid).catch(() => null)) ?? initialRecord;
  let attemptBaseline = Math.min(record.sync_attempt_count ?? 0, MAX_SYNC_ATTEMPTS);
  let attemptWasCounted = false;

  try {
    // Ein neuer Retry ersetzt den alten Fehlerzustand sofort sichtbar. So bleibt
    // während Dokumentprüfung/Recovery kein veraltetes „Server nicht erreichbar“
    // stehen, obwohl die aktuelle Serverprüfung bereits erfolgreich ist.
    record = await updateRecord(record, { last_error: null, next_sync_attempt_at: null });
    window.dispatchEvent(new Event("materialentnahme:status-refresh"));
    record = await markSyncPhase(record, "backend_check", "started", "Server-Erreichbarkeit wird geprüft.");
    // navigator.onLine sagt nur etwas über die Netzwerkschnittstelle aus. Ein
    // gestopptes Backend darf daher keinen Sync-Versuch verbrauchen.
    const backendAvailability = await backendAvailabilityForSync();
    if (backendAvailability !== "ok") {
      throw new Error(backendAvailabilityMessage(backendAvailability));
    }
    maybeThrowSimulatedSyncFailure(record, "backend_check");
    record = await markSyncPhase(record, "backend_check", "completed", "Server erreichbar.");

    const rawPreviousAttempts = record.sync_attempt_count ?? 0;
    const previousAttempts = record.deleted_at
      ? rawPreviousAttempts % MAX_SYNC_ATTEMPTS
      : Math.min(rawPreviousAttempts, MAX_SYNC_ATTEMPTS);
    attemptBaseline = previousAttempts;
    if (!record.deleted_at && previousAttempts >= MAX_SYNC_ATTEMPTS) {
      throw new Error("Maximale Anzahl automatischer Sync-Versuche erreicht. Manuelle Recovery erforderlich.");
    }

    // Fehlende deterministisch erzeugbare Pflichtdokumente werden aus den fachlichen
    // Entnahmedaten rekonstruiert, bevor ein Sync-Versuch gezählt wird.
    record = await markSyncPhase(record, "prepare_documents", "started", "Pflichtdokumente werden geprüft bzw. rekonstruiert.");
    const documentRefreshWasRequired = Boolean(record.document_server_refresh_required) || record.document_artifact_version !== WITHDRAWAL_DOCUMENT_ARTIFACT_VERSION;
    record = await ensureRequiredDocuments(record);
    const forceDocumentReplacement = documentRefreshWasRequired || Boolean(record.document_server_refresh_required);
    record = await markSyncPhase(record, "prepare_documents", "completed", `PDF ${record.pdf_blob ? "vorhanden" : "fehlt"} · Vorschau ${record.pdf_preview_blob ? "vorhanden" : "fehlt"}.`);

    // Ein vorhandener IndexedDB-Blob ist auf Safari/iOS nicht automatisch auch
    // lesbar. Vor dem Zählen eines Sync-Versuchs werden deshalb die echten Bytes
    // geprüft und defekte Pflichtdokumente innerhalb desselben Vorgangs neu erzeugt.
    record = await markSyncPhase(record, "blob_preflight", "started", "PDF- und Vorschau-Blob werden auf tatsächliche Lesbarkeit geprüft.");
    record = await verifyRequiredDocumentBlobs(record);
    record = await markSyncPhase(record, "blob_preflight", "completed", `PDF ${record.pdf_blob_health ?? "unknown"} · Vorschau ${record.preview_blob_health ?? "unknown"}.`);

    // Recovery bleibt eine Schutzebene, darf den fachlichen Server-Sync aber nicht
    // blockieren. Bereits identische Snapshots werden nicht erneut mit großen
    // Safari/iOS-Blobs überschrieben; Änderungen werden detailliert instrumentiert.
    record = await persistRecoverySnapshotForSync(record);

    const attemptCount = Math.min(previousAttempts + 1, MAX_SYNC_ATTEMPTS);
    record =
      await updateRecord(
        record,
        {
          sync_status:
            "syncing",

          sync_attempt_count:
            attemptCount,

          next_sync_attempt_at:
            null,

          last_sync_attempt_at:
            new Date()
              .toISOString(),

          last_error:
            null,
        }
      );
    attemptWasCounted = true;
    record = await markSyncPhase(record, "attempt_started", "completed", `Sync-Versuch ${attemptCount}/${MAX_SYNC_ATTEMPTS} gestartet.`);
    window.dispatchEvent(new Event("materialentnahme:status-refresh"));

    // -----------------------------------------------------
    // 1. Metadaten
    // -----------------------------------------------------

    record = await markSyncPhase(record, "metadata_lookup", "started", "Lokale Benutzerzuordnung und vorhandener Serverzustand werden geprüft.");
    const currentUser = getLastKnownAuthUser();
    if (!currentUser || currentUser.id !== record.owner_user_id) {
      throw new Error("Offline-Vorgang gehört zu einem anderen Benutzer und wurde nicht synchronisiert.");
    }
    // Bei einem verlorenen Server-Response kann der Datensatz serverseitig bereits
    // existieren, obwohl der Client noch keine Server-ID gespeichert hat. Vor einem
    // erneuten POST wird deshalb zuerst über die stabile UUID nach einem bereits
    // angenommenen Vorgang gesucht und dieser fortgesetzt.
    let metadata: Awaited<ReturnType<typeof syncWithdrawalMetadata>> | null = null;
    let finalizedDraftNow = false;

    // Wurde derselbe Vorgang zuvor als Server-Entwurf synchronisiert, muss er
    // beim Abschluss ausdrücklich finalisiert werden. Sonst würde der Server
    // trotz PDF/E-Mail weiterhin den fachlichen Status „Entwurf“ liefern.
    if (record.record_type === "completed" && record.server_withdrawal_id) {
      const finalizeResponse = await fetch(`/api/withdrawals/drafts/${encodeURIComponent(record.client_uuid)}/finalize`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(record.data),
      });
      if (!finalizeResponse.ok && finalizeResponse.status !== 409 && finalizeResponse.status !== 404) {
        throw new Error(`Server-Entwurf konnte nicht abgeschlossen werden (${finalizeResponse.status}).`);
      }
      finalizedDraftNow = finalizeResponse.ok || finalizeResponse.status === 409;
    }

    if (record.sync_status === "syncing" || record.sync_status === "error" || record.server_withdrawal_id) {
      const existing = await getWithdrawalSyncStatus(record.client_uuid);
      if (existing) {
        if (existing.owner_user_id !== record.owner_user_id) {
          throw new Error("Server hat die Entnahme einem anderen Benutzer zugeordnet.");
        }
        // Ein Edit darf den Versionsendpoint niemals anhand eines allgemeinen
        // Status-Lookups überspringen. Nur /versions kann idempotenten Retry vs.
        // fremde Zwischenänderung sauber unterscheiden und 409 liefern.
        if (record.record_type !== "edit") {
          metadata = {
            created: false,
            withdrawal_id: existing.withdrawal_id,
            client_uuid: existing.client_uuid,
            withdrawal_number: existing.withdrawal_number,
            owner_user_id: existing.owner_user_id,
            owner_username: record.owner_username,
            current_version: existing.current_version,
            sync_status: existing.sync_status,
          };
        }
      }
    }

    record = await markSyncPhase(record, "metadata_lookup", "completed", metadata ? `Vorhandener Serverdatensatz gefunden · SID ${metadata.withdrawal_id}.` : "Kein fortsetzbarer Serverdatensatz gefunden.");
    record = await markSyncPhase(record, "metadata_create_or_resume", "started", metadata ? "Vorhandener Serverdatensatz wird fortgesetzt." : "Serverdatensatz wird angelegt bzw. Version synchronisiert.");
    maybeThrowSimulatedSyncFailure(record, "metadata_create_or_resume");
    if (!metadata) {
      metadata = record.record_type === "edit"
        ? await syncWithdrawalVersion(
            record.client_uuid,
            record.base_version ?? (() => { throw new Error("Basisversion der Offline-Bearbeitung fehlt."); })(),
            { ...record.data, change_description: "Entnahme bearbeitet." },
          )
        : await syncWithdrawalMetadata({
            ...record.data,
            requested_withdrawal_number: record.local_withdrawal_number,
          });
    }
    record = await markSyncPhase(record, "metadata_create_or_resume", "completed", `Serverdatensatz bereit · SID ${metadata.withdrawal_id} · Entnahme #${metadata.withdrawal_number}.`);
    if (metadata.owner_user_id !== record.owner_user_id) {
      throw new Error("Server hat die Entnahme einem anderen Benutzer zugeordnet.");
    }
    rememberConfirmedWithdrawalNumber(record.owner_user_id, metadata.withdrawal_number);

    record = await markSyncPhase(record, "metadata_persist", "started", "Server-ID und Server-Entnahmenummer werden lokal gespeichert.");
    record =
      await updateRecord(
        record,
        {
          server_withdrawal_id:
            metadata.withdrawal_id,

          server_withdrawal_number:
            metadata.withdrawal_number,
          local_withdrawal_number:
            metadata.withdrawal_number,
        }
      );
    record = await markSyncPhase(record, "metadata_persist", "completed", `SID ${record.server_withdrawal_id ?? "–"} lokal gespeichert.`);
    // v0.82.3: Diagnoseartefakte wurden vor dem Server-Sync erzeugt und kennen
    // deshalb anfangs noch keine SID. Direkt nach der SID-Vergabe werden JSON
    // und Debug-Bild lokal aktualisiert, bevor die Diagnose zum Server geht.
    record = await refreshDiagnosticIdentity(record);

    // Ein lokal gelöschter Vorgang wird bewusst erst serverseitig angelegt/gefunden
    // und anschließend idempotent soft-gelöscht. Dadurch bleibt auch eine zuvor
    // nie synchronisierte Entnahmenummer historisch nachvollziehbar und erhält
    // erst vom Server ihre echte SID. Dokumente bleiben lokal erhalten.
    if (record.deleted_at) {
      await softDeleteServerWithdrawal(metadata.withdrawal_id);
      await updateRecord(record, {
        sync_status: "synced",
        synced_at: new Date().toISOString(),
        next_sync_attempt_at: null,
        last_error: null,
      });
      await deleteRecoverySnapshot(record.client_uuid);
      await resolveSyncFailureReport(record.client_uuid);
      void verifyServerArchiveAndCompact(record.client_uuid).catch(() => 0);
      window.dispatchEvent(new Event("materialentnahme:status-refresh"));
      return;
    }

    // Vor dem Dokument-Upload nochmals den tatsächlichen Serverstand lesen.
    // Bei einem Recovery-Retry werden dadurch bereits erfolgreich archivierte
    // Dateien nicht erneut übertragen.
    record = await markSyncPhase(record, "server_files", "started", `Serverdateien für SID ${metadata.withdrawal_id} werden geprüft.`);
    maybeThrowSimulatedSyncFailure(record, "server_files");
    const serverFileState = await getWithdrawalSyncStatus(record.client_uuid);
    const serverFiles = await listWithdrawalServerFiles(metadata.withdrawal_id);
    const currentVersion = metadata.current_version;
    const currentServerFileTypes = new Set(
      serverFiles.filter((file) => file.version === currentVersion).map((file) => file.file_type)
    );
    const pdfPresent = currentServerFileTypes.has("pdf") || Boolean(serverFileState?.pdf_present);
    const previewPresent = currentServerFileTypes.has("pdf_preview") || Boolean(serverFileState?.preview_present);
    const labelPresent = currentServerFileTypes.has("label") || Boolean(serverFileState?.label_present);
    record = await markSyncPhase(record, "server_files", "completed", `Serverdateien: PDF ${pdfPresent ? "Ja" : "Nein"} · Vorschau ${previewPresent ? "Ja" : "Nein"} · Kontrollbild ${labelPresent ? "Ja" : "Nein"}.`);

    // -----------------------------------------------------
    // 2. PDF
    // -----------------------------------------------------

    // Eine fehlende lokale PDF ist kein fachlicher Sync-Blocker: ensureRequiredDocuments()
    // rekonstruiert PDF/WebP aus den Entnahmedaten. Für Safari/iOS verwenden wir
    // anschließend den robusteren Raw-Upload statt multipart/FormData.
    if (!record.pdf_blob || !record.pdf_filename) {
      record = await ensureRequiredDocuments(record);
    }

    if (!pdfPresent || finalizedDraftNow || forceDocumentReplacement) {
      record = await markSyncPhase(record, "pdf_upload", "started", finalizedDraftNow ? "Finale PDF ersetzt die zuvor gespeicherte Draft-PDF." : forceDocumentReplacement ? "Validierte PDF ersetzt ein älteres Dokumentartefakt auf dem Server." : "PDF wird zum Server übertragen.");
      maybeThrowSimulatedSyncFailure(record, "pdf_upload");
      const pdfBlob = record.pdf_blob;
      const pdfFilename = record.pdf_filename;
      if (!pdfBlob || !pdfFilename) throw new Error("Dokument konnte nicht erzeugt: PDF ist trotz vollständiger Entnahmedaten nicht verfügbar.");
      try {
        await uploadWithdrawalFileRaw(metadata.withdrawal_id, "pdf", pdfBlob, pdfFilename);
        record = await markSyncPhase(record, "pdf_upload", "completed", `${pdfFilename} · ${pdfBlob.size} Byte übertragen.`);
      } catch (error) {
        const firstError = blobReadErrorText(error);
        // WebKit kann einen IndexedDB-Blob zwischen Preflight und Upload ungültig
        // werden lassen. Einmal innerhalb desselben Versuchs frisch regenerieren
        // und direkt aus dem neuen In-Memory-Blob erneut hochladen.
        if (/object can not be found|object cannot be found|binärdatei gelesen|blob|not found here/i.test(firstError)) {
          record = await markSyncPhase(record, "document_self_heal", "started", `PDF-Upload konnte lokalen Blob nicht lesen: ${firstError}`);
          record = await regenerateRequiredDocuments(record, `PDF-Upload: ${firstError}`);
          const healedPdf = record.pdf_blob;
          const healedFilename = record.pdf_filename;
          if (!healedPdf || !healedFilename) throw new Error("Self-Healing konnte keine neue PDF erzeugen.", { cause: error });
          await uploadWithdrawalFileRaw(metadata.withdrawal_id, "pdf", healedPdf, healedFilename);
          record = await markSyncPhase(record, "document_self_heal", "completed", `PDF unmittelbar neu erzeugt und hochgeladen · ${healedPdf.size} Byte.`);
          record = await markSyncPhase(record, "pdf_upload", "completed", `${healedFilename} · nach Self-Healing übertragen.`);
        } else {
          throw error;
        }
      }
    } else {
      record = await markSyncPhase(record, "pdf_upload", "completed", "PDF bereits auf dem Server vorhanden; kein Upload nötig.");
    }

    const previewBlob = record.pdf_preview_blob;
    const previewFilename = record.pdf_preview_filename;
    if ((!previewPresent || finalizedDraftNow || forceDocumentReplacement) && previewBlob && previewFilename) {
      record = await markSyncPhase(record, "preview_upload", "started", finalizedDraftNow ? "Finale PDF-Vorschau ersetzt die Draft-Vorschau." : "PDF-Vorschau wird zum Server übertragen.");
      maybeThrowSimulatedSyncFailure(record, "preview_upload");
      await uploadWithdrawalFileRaw(metadata.withdrawal_id, "pdf_preview", previewBlob, previewFilename);
      record = await markSyncPhase(record, "preview_upload", "completed", "PDF-Vorschau übertragen.");
    } else if (previewPresent && !forceDocumentReplacement) {
      record = await markSyncPhase(record, "preview_upload", "completed", "PDF-Vorschau bereits auf dem Server vorhanden.");
    } else {
      throw new Error("Dokument konnte nicht erzeugt: PDF-Vorschau fehlt trotz vollständiger Entnahmedaten.");
    }

    const manualBusinessOnly = (record.data.capture_mode ?? "automatic") === "manual";
    if (!manualBusinessOnly) {
    // -----------------------------------------------------
      // 3. Etikett
      // -----------------------------------------------------

      record = await markSyncPhase(record, "label_upload", "started", labelPresent ? "Kontrollbild ist bereits auf dem Server vorhanden." : "Kontrollbild wird geprüft bzw. übertragen.");
      maybeThrowSimulatedSyncFailure(record, "label_upload");
      if (!labelPresent && record.label_blob && record.label_filename) {
        try {
          await uploadWithdrawalFileRaw(metadata.withdrawal_id, "label", record.label_blob, record.label_filename);
          record = await markSyncPhase(record, "label_upload", "completed", "Kontrollbild übertragen.");
        } catch (error) {
          record = await markSyncPhase(record, "label_upload", "completed", `Kontrollbild konnte lokal nicht gelesen/übertragen werden und wurde als optional übersprungen · ${blobReadErrorText(error)}`);
        }
      } else {
        record = await markSyncPhase(record, "label_upload", "completed", labelPresent ? "Kontrollbild bereits auf dem Server vorhanden." : "Kein lokales Kontrollbild vorhanden; Sync darf trotzdem fortgesetzt werden.");
      }

      // Die unveränderte Kameraaufnahme ist Grundlage für den späteren Vision Optimizer.
      // Sie bleibt technisch optional für den fachlichen Sync: Ein Uploadfehler darf
      // Materialentnahme, Archiv-PDF oder E-Mail niemals blockieren.
      const sourcePresent = currentServerFileTypes.has("source");
      record = await markSyncPhase(record, "source_upload", "started", sourcePresent ? "Originalaufnahme ist bereits auf dem Server vorhanden." : "Originalaufnahme wird für die Vision-Analyse archiviert.");
      if (!sourcePresent && record.source_blob && record.source_filename) {
        try {
          await uploadWithdrawalFileRaw(metadata.withdrawal_id, "source", record.source_blob, record.source_filename);
          record = await markSyncPhase(record, "source_upload", "completed", `Originalaufnahme übertragen · ${record.source_blob.size} Byte.`);
        } catch (error) {
          record = await markSyncPhase(record, "source_upload", "completed", `Originalaufnahme konnte nicht übertragen werden; fachlicher Sync läuft weiter · ${blobReadErrorText(error)}`);
        }
      } else {
        record = await markSyncPhase(record, "source_upload", "completed", sourcePresent ? "Originalaufnahme bereits auf dem Server vorhanden." : "Keine lokale Originalaufnahme vorhanden; ältere/offline fortgesetzte Vorgänge bleiben synchronisierbar.");
      }

      // Exakter Detection-Eingang (harter Scanrahmen-Crop). Er wird getrennt von
      // der unveränderten Originalaufnahme archiviert, damit der spätere Node-Replay
      // exakt die Bildstufe erneut ausführen kann, auf der OpenCV produktiv lief.
      const detectionSourcePresent = currentServerFileTypes.has("detection_source");
      record = await markSyncPhase(record, "detection_source_upload", "started", detectionSourcePresent ? "Detection-Quelle ist bereits auf dem Server vorhanden." : "Detection-Quelle wird für reproduzierbaren Vision-Replay archiviert.");
      if (!detectionSourcePresent && record.detection_source_blob && record.detection_source_filename) {
        try {
          await uploadWithdrawalFileRaw(metadata.withdrawal_id, "detection_source", record.detection_source_blob, record.detection_source_filename);
          record = await markSyncPhase(record, "detection_source_upload", "completed", `Detection-Quelle übertragen · ${record.detection_source_blob.size} Byte.`);
        } catch (error) {
          record = await markSyncPhase(record, "detection_source_upload", "completed", `Detection-Quelle konnte nicht übertragen werden; fachlicher Sync läuft weiter · ${blobReadErrorText(error)}`);
        }
      } else {
        record = await markSyncPhase(record, "detection_source_upload", "completed", detectionSourcePresent ? "Detection-Quelle bereits auf dem Server vorhanden." : "Keine lokale Detection-Quelle vorhanden; ältere Vorgänge bleiben synchronisierbar.");
      }

      // Verlustfreier RGBA/OpenCV-Eingang. Bei neuen Scans ist dies exakt die
      // 1600px-Analyseebene aus dem Browser. Der VO kann dadurch spätere Replays
      // pixelidentisch starten, ohne Canvas-/Chromium-Resize rekonstruieren zu müssen.
      const analysisInputPresent = currentServerFileTypes.has("analysis_input");
      record = await markSyncPhase(record, "analysis_input_upload", "started", analysisInputPresent ? "OpenCV-Analysebild ist bereits auf dem Server vorhanden." : "Exaktes OpenCV-Analysebild wird für schnelle Replays archiviert.");
      if (!analysisInputPresent && record.analysis_input_blob && record.analysis_input_filename) {
        try {
          await uploadWithdrawalFileRaw(metadata.withdrawal_id, "analysis_input", record.analysis_input_blob, record.analysis_input_filename);
          record = await markSyncPhase(record, "analysis_input_upload", "completed", `OpenCV-Analysebild übertragen · ${record.analysis_input_blob.size} Byte.`);
        } catch (error) {
          record = await markSyncPhase(record, "analysis_input_upload", "completed", `OpenCV-Analysebild konnte nicht übertragen werden; fachlicher Sync läuft weiter · ${blobReadErrorText(error)}`);
        }
      } else {
        record = await markSyncPhase(record, "analysis_input_upload", "completed", analysisInputPresent ? "OpenCV-Analysebild bereits vorhanden." : "Kein exaktes OpenCV-Analysebild vorhanden; Legacy-Replay bleibt möglich.");
      }

      // Exakte lossless Browser-RGBA-Oberfläche unmittelbar vor dem Perspective Warp.
      // JPEG-/Canvas-Decoding ist geräteübergreifend nicht bitidentisch; deshalb
      // wird diese Production-Pixeloberfläche separat für Replay/E2E archiviert.
      const rectificationInputPresent = currentServerFileTypes.has("rectification_input");
      record = await markSyncPhase(record, "rectification_input_upload", "started", rectificationInputPresent ? "Rectification-Input ist bereits auf dem Server vorhanden." : "Exakter Browser-Rectification-Input wird für E2E-Replay archiviert.");
      if (!rectificationInputPresent && record.rectification_input_blob && record.rectification_input_filename) {
        try {
          await uploadWithdrawalFileRaw(metadata.withdrawal_id, "rectification_input", record.rectification_input_blob, record.rectification_input_filename);
          record = await markSyncPhase(record, "rectification_input_upload", "completed", `Rectification-Input übertragen · ${record.rectification_input_blob.size} Byte.`);
        } catch (error) {
          record = await markSyncPhase(record, "rectification_input_upload", "completed", `Rectification-Input konnte nicht übertragen werden; fachlicher Sync läuft weiter · ${blobReadErrorText(error)}`);
        }
      } else {
        record = await markSyncPhase(record, "rectification_input_upload", "completed", rectificationInputPresent ? "Rectification-Input bereits vorhanden." : "Kein Rectification-Input vorhanden; Legacy-Replay bleibt möglich.");
      }

      // LIVE-Lernartefakt: wenige stark verkleinerte Preview-Frames plus Heuristik-Metadaten.
      // Reines Lern-/Diagnoseartefakt und daher immer Best-Effort.
      // Exakter perspektivisch entzerrter OCR-Warp. Rein technisch/lernend und
      // best-effort; ein Fehler darf den fachlichen Sync niemals blockieren.
      const ocrWarpPresent = currentServerFileTypes.has("ocr_warp");
      record = await markSyncPhase(record, "ocr_warp_upload", "started", ocrWarpPresent ? "OCR-Warp ist bereits auf dem Server vorhanden." : "Exakter OCR-Warp wird für Field-Level-Lernen archiviert.");
      if (!ocrWarpPresent && record.ocr_warp_blob && record.ocr_warp_filename) {
        try {
          await uploadWithdrawalFileRaw(metadata.withdrawal_id, "ocr_warp", record.ocr_warp_blob, record.ocr_warp_filename);
          record = await markSyncPhase(record, "ocr_warp_upload", "completed", `OCR-Warp übertragen · ${record.ocr_warp_blob.size} Byte.`);
        } catch (error) {
          record = await markSyncPhase(record, "ocr_warp_upload", "completed", `OCR-Warp konnte nicht übertragen werden; fachlicher Sync läuft weiter · ${blobReadErrorText(error)}`);
        }
      } else {
        record = await markSyncPhase(record, "ocr_warp_upload", "completed", ocrWarpPresent ? "OCR-Warp bereits vorhanden." : "Kein OCR-Warp vorhanden; normales VO-/OCR-Verhalten bleibt erhalten.");
      }

      // Strukturierte Step-2-/Retake-Rückmeldung. Sie ist ausschließlich Lernsignal
      // für OCR/Qualitätsanalyse und ausdrücklich keine Geometry-Ground-Truth.
      const visionFeedbackPresent = currentServerFileTypes.has("vision_feedback");
      record = await markSyncPhase(record, "vision_feedback_upload", "started", visionFeedbackPresent ? "Vision-Feedback ist bereits auf dem Server vorhanden." : "Vision-Feedback wird optional archiviert.");
      if (!visionFeedbackPresent && record.vision_feedback_blob && record.vision_feedback_filename) {
        try {
          await uploadWithdrawalFileRaw(metadata.withdrawal_id, "vision_feedback", record.vision_feedback_blob, record.vision_feedback_filename);
          record = await markSyncPhase(record, "vision_feedback_upload", "completed", `Vision-Feedback übertragen · ${record.vision_feedback_blob.size} Byte.`);
        } catch (error) {
          record = await markSyncPhase(record, "vision_feedback_upload", "completed", `Vision-Feedback konnte nicht übertragen werden; fachlicher Sync läuft weiter · ${blobReadErrorText(error)}`);
        }
      } else {
        record = await markSyncPhase(record, "vision_feedback_upload", "completed", visionFeedbackPresent ? "Vision-Feedback bereits vorhanden." : "Kein Vision-Feedback vorhanden; ältere/fortgesetzte Vorgänge bleiben vollständig synchronisierbar.");
      }

      const liveTrainingPresent = currentServerFileTypes.has("live_training");
      record = await markSyncPhase(record, "live_training_upload", "started", liveTrainingPresent ? "LIVE-Trainingsdaten bereits auf dem Server vorhanden." : "LIVE-Trainingsdaten werden optional archiviert.");
      if (!liveTrainingPresent && record.live_training_blob && record.live_training_filename) {
        try {
          await uploadWithdrawalFileRaw(metadata.withdrawal_id, "live_training", record.live_training_blob, record.live_training_filename);
          record = await markSyncPhase(record, "live_training_upload", "completed", `LIVE-Trainingsdaten übertragen · ${record.live_training_blob.size} Byte.`);
        } catch (error) {
          record = await markSyncPhase(record, "live_training_upload", "completed", `LIVE-Trainingsdaten konnten nicht übertragen werden; fachlicher Sync läuft weiter · ${blobReadErrorText(error)}`);
        }
      } else {
        record = await markSyncPhase(record, "live_training_upload", "completed", liveTrainingPresent ? "LIVE-Trainingsdaten bereits vorhanden." : "Keine LIVE-Trainingsdaten vorhanden; Sync läuft normal weiter.");
      }

      // Admin-Diagnose ist best effort: immer archivieren, wenn lokal vorhanden,
      // aber niemals den Abschluss oder E-Mail-Versand blockieren.
      record = await markSyncPhase(record, "debug_upload", "started", "Optionale Admin-Diagnoseartefakte werden geprüft.");
      const debugPresent = currentServerFileTypes.has("debug");
      const debugDataPresent = currentServerFileTypes.has("debug_data");
      if (!debugPresent && record.debug_image_blob && record.debug_image_filename) {
        try { await uploadWithdrawalFileRaw(metadata.withdrawal_id, "debug", record.debug_image_blob, record.debug_image_filename); } catch { /* Diagnose blockiert nie */ }
      }
      if (!debugDataPresent && record.debug_data_blob && record.debug_data_filename) {
        try { await uploadWithdrawalFileRaw(metadata.withdrawal_id, "debug_data", record.debug_data_blob, record.debug_data_filename); } catch { /* Diagnose blockiert nie */ }
      }
      record = await markSyncPhase(record, "debug_upload", "completed", "Optionale Diagnoseartefakte verarbeitet; Fehler blockieren den Sync nicht.");
      } else {
      // Manuelle Entnahme: absichtlich keinerlei Vision-/VO-/Diagnoseartefakte.
      record = await markSyncPhase(record, "label_upload", "completed", "Manuelle Entnahme · Vision-Artefakte werden nicht erzeugt oder übertragen.");
      record = await markSyncPhase(record, "debug_upload", "completed", "Manuelle Entnahme · keine Diagnoseartefakte.");
    }

    // -----------------------------------------------------
    // 4. Sync abschließen
    // -----------------------------------------------------

    record = await markSyncPhase(record, "complete", "started", "Server-Synchronisierung wird final abgeschlossen.");
    maybeThrowSimulatedSyncFailure(record, "complete");
    const completion =
      await completeWithdrawalSync(
        record.client_uuid,
        { diagnosticEmailTest: isDiagnosticEmailTest(record) },
      );
    record = await markSyncPhase(record, "complete", "completed", `Server bestätigt vollständigen Sync · ${completion.synced_at}.`);

    record = await updateRecord(
      record,
      {
        sync_status:
          "synced",

        synced_at:
          completion.synced_at,

        next_sync_attempt_at:
          null,

        sync_attempt_count:
          0,

        document_server_refresh_required:
          false,

        // Versandprobleme sind kein Synchronisierungsfehler. Ein erfolgreich
        // synchronisierter Datensatz bleibt lokal fehlerfrei; Mailstatus und
        // fehlende Empfängerkonfiguration werden separat dargestellt.
        last_error:
          null,
      }
    );
    record = await markSyncPhase(record, "done", "completed", "Synchronisierung vollständig abgeschlossen.");
    // Erst jetzt darf die geschützte Arbeitskopie entfernt werden.
    await deleteRecoverySnapshot(record.client_uuid);
    await resolveSyncFailureReport(record.client_uuid);
    void verifyServerArchiveAndCompact(record.client_uuid).catch(() => 0);
    window.dispatchEvent(new Event("materialentnahme:status-refresh"));
  } catch (error) {
    const message =
      error instanceof Error
        ? error.message
        : "Unbekannter Synchronisierungsfehler.";

    const failedPhase = record.sync_phase ?? "idle";
    record = await markSyncPhase(record, failedPhase, "failed", message).catch(() => record);
    const attempts = Math.min(record.sync_attempt_count ?? 1, MAX_SYNC_ATTEMPTS);
    const failureKind = classifySyncError(error);
    const connectivityError = failureKind === "connectivity";
    const terminalError = failureKind === "terminal" || failureKind === "conflict";
    const structuralLocalError = isStructuralLocalSyncError(message);
    const storedMessage = structuralLocalError
      ? `Lokaler Datenfehler · ${message}`
      : failureKind === "conflict"
        ? `Bearbeitungskonflikt · ${message}`
        : message;
    const effectiveAttempts = connectivityError && attemptWasCounted ? attemptBaseline : attempts;
    const nextAttemptAt = !terminalError && !connectivityError && effectiveAttempts < MAX_SYNC_ATTEMPTS
      ? new Date(Date.now() + syncRetryDelayMs(effectiveAttempts)).toISOString()
      : null;

    await updateRecord(
      record,
      {
        sync_status: connectivityError ? "pending" : "error",
        sync_attempt_count: effectiveAttempts,
        next_sync_attempt_at: nextAttemptAt,
        last_error: storedMessage,
      }
    );
    if (record.server_withdrawal_id && !connectivityError) {
      void fetch(`/api/withdrawals/${record.server_withdrawal_id}/sync-events`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ event_type: "sync_attempt_failed", detail: message, attempt_number: attempts }),
      }).catch(() => undefined);
    }
    window.dispatchEvent(new Event("materialentnahme:status-refresh"));

    throw error;
  }
}


export async function syncSingleWithdrawal(initialRecord: LocalWithdrawalRecord): Promise<void> {
  const existing = activeWithdrawalSyncs.get(initialRecord.client_uuid);
  if (existing) return existing;

  const leaseOwner = createLocalSyncLeaseOwner("window");
  const leasedRecord = await acquireLocalWithdrawalSyncLease(initialRecord.client_uuid, leaseOwner);
  // Ein anderer Tab/Fenster-Kontext oder der Service Worker verarbeitet denselben
  // Vorgang bereits. Das ist kein Fehler und darf keinen weiteren 1/9-Versuch
  // erzeugen; der nächste Queue-Lauf sieht anschließend den neuen Zustand.
  if (!leasedRecord) return;

  const heartbeat = window.setInterval(() => {
    void renewLocalWithdrawalSyncLease(initialRecord.client_uuid, leaseOwner).then((renewed) => {
      if (!renewed) console.warn(`Sync-Lease für ${initialRecord.client_uuid} konnte nicht verlängert werden.`);
    }).catch(() => undefined);
  }, SYNC_LEASE_HEARTBEAT_MS);

  const operation = syncSingleWithdrawalUnlocked(leasedRecord).finally(async () => {
    window.clearInterval(heartbeat);
    await releaseLocalWithdrawalSyncLease(initialRecord.client_uuid, leaseOwner).catch(() => undefined);
    if (activeWithdrawalSyncs.get(initialRecord.client_uuid) === operation) {
      activeWithdrawalSyncs.delete(initialRecord.client_uuid);
    }
  });
  activeWithdrawalSyncs.set(initialRecord.client_uuid, operation);
  return operation;
}

export async function reportExhaustedSyncFailures(records?: LocalWithdrawalRecord[]): Promise<void> {
  if (typeof navigator !== "undefined" && !navigator.onLine) return;
  const currentUser = getLastKnownAuthUser();
  if (!currentUser) return;
  const all = records ?? await getAllLocalWithdrawals();
  const exhausted = all.filter((record) =>
    !record.deleted_at
    && record.record_type !== "draft"
    && record.owner_user_id === currentUser.id
    && record.sync_status !== "synced"
    && (record.sync_attempt_count ?? 0) >= MAX_SYNC_ATTEMPTS
  );
  await Promise.allSettled(exhausted.map(async (record) => {
    const response = await fetch("/api/notices/sync-failure", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        client_uuid: record.client_uuid,
        withdrawal_number: record.local_withdrawal_number ?? record.server_withdrawal_number ?? null,
        material_number: record.data.material_number || null,
        material_text: record.data.material_text || null,
        attempts: Math.min(record.sync_attempt_count ?? MAX_SYNC_ATTEMPTS, 99),
        last_error: record.last_error ?? null,
        event_key: record.last_sync_attempt_at ?? `attempt-${record.sync_attempt_count ?? MAX_SYNC_ATTEMPTS}`,
      }),
    });
    if (!response.ok) throw new Error(`Sync-Fehlerreport konnte nicht gemeldet werden (${response.status}).`);
  }));
}


async function resolveSyncFailureReport(clientUuid: string): Promise<void> {
  try {
    await fetch(`/api/notices/sync-failure/${clientUuid}/resolve`, { method: "POST", credentials: "include" });
  } catch { /* Der fachliche Sync/Soft-Delete darf dadurch nie scheitern. */ }
}


export async function softDeleteLocalWithdrawalByUuid(clientUuid: string): Promise<void> {
  const currentUser = getLastKnownAuthUser();
  if (!currentUser) throw new Error("Keine aktive Benutzersitzung.");
  const record = await getLocalWithdrawal(clientUuid);
  if (!record || record.owner_user_id !== currentUser.id) throw new Error("Lokaler Vorgang nicht gefunden.");
  if (record.record_type === "draft") throw new Error("Entwürfe werden separat verwaltet.");
  if (record.deleted_at) return;

  const deleted = await updateRecord(record, {
    deleted_at: new Date().toISOString(),
    sync_status: "pending",
    sync_attempt_count: 0,
    last_sync_attempt_at: null,
    next_sync_attempt_at: null,
    last_error: null,
  });
  await saveRecoverySnapshot(deleted);
  void resolveSyncFailureReport(deleted.client_uuid);
  window.dispatchEvent(new Event("materialentnahme:status-refresh"));
  // Sofort versuchen, wenn der Server verfügbar ist; ein Fehlschlag ändert den
  // sichtbaren fachlichen Status "Gelöscht" nicht.
  void syncSingleWithdrawal(deleted).catch(() => undefined);
}

export async function retryLocalSyncByUuid(clientUuid: string): Promise<void> {
  const currentUser = getLastKnownAuthUser();
  if (!currentUser) throw new Error("Keine aktive Benutzersitzung.");
  const existing = await getLocalWithdrawal(clientUuid);
  const snapshots = await getAllRecoverySnapshots();
  const snapshot = snapshots.find((candidate) => candidate.client_uuid === clientUuid && candidate.owner_user_id === currentUser.id) ?? null;
  const source = existing?.owner_user_id === currentUser.id ? existing : snapshot;
  if (!source) throw new Error("Lokaler Vorgang nicht gefunden.");

  // Entwürfe besitzen denselben sichtbaren Syncstatus und damit auch denselben
  // manuellen „Erneut versuchen“-Einstieg. Sie laufen fachlich weiterhin über
  // ihren idempotenten Draft-Endpunkt und nicht durch den Completed-Recoverypfad.
  if (source.record_type === "draft") {
    await assertBackendAvailableForManualRetry();
    const resetDraft = await updateRecord(source, {
      sync_status: "pending", sync_attempt_count: 0, last_sync_attempt_at: null, next_sync_attempt_at: null, last_error: null,
    });
    void registerMaterialentnahmeBackgroundSync();
    window.dispatchEvent(new Event("materialentnahme:status-refresh"));
    await syncLocalDraft(resetDraft);
    return;
  }

  // Ein manueller Neuversuch darf den 9/9-Zustand erst verlassen, wenn der
  // Server tatsächlich erreichbar ist. So startet bei Offlinebetrieb keine
  // neue 1/9→9/9-Kette und der Recovery-Hinweis verschwindet nicht irreführend.
  await assertBackendAvailableForManualRetry();

  const prepared = await restoreLocalRequiredDocumentsByUuid(clientUuid);
  const reset = await updateRecord(prepared, {
    sync_status: "pending", sync_attempt_count: 0, last_sync_attempt_at: null, next_sync_attempt_at: null, last_error: null,
  });
  window.dispatchEvent(new Event("materialentnahme:status-refresh"));

  if (reset.server_withdrawal_id) {
    void fetch(`/api/withdrawals/${reset.server_withdrawal_id}/sync-events`, {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ event_type: "sync_retry_requested", detail: "Manueller Neuversuch auf dem Benutzergerät gestartet.", attempt_number: 0 }),
    }).catch(() => undefined);
  }
  await syncSingleWithdrawal(reset);
}


export async function recoverIncompleteServerSyncs(
  alreadyProcessed: Set<string> = new Set(),
): Promise<{ recovered: number; blocked: number }> {
  const currentUser = getLastKnownAuthUser();
  if (!currentUser) return { recovered: 0, blocked: 0 };

  const [serverItems, allLocal, recoverySnapshots] = await Promise.all([
    fetchIncompleteServerSyncs(),
    getAllLocalWithdrawals(),
    getAllRecoverySnapshots(),
  ]);
  const mergedByUuid = new Map<string, LocalWithdrawalRecord>();
  for (const record of recoverySnapshots) mergedByUuid.set(record.client_uuid, record);
  for (const record of allLocal) mergedByUuid.set(record.client_uuid, record);
  const localRecords = [...mergedByUuid.values()].filter((record) => record.owner_user_id === currentUser.id);
  let recovered = 0;
  let blocked = 0;

  for (const serverItem of serverItems) {
    if (alreadyProcessed.has(serverItem.uuid)) continue;
    const local = matchLocalRecoveryRecord(serverItem, localRecords);
    if (!local || local.record_type === "draft") {
      blocked += 1;
      continue;
    }
    if ((local.sync_attempt_count ?? 0) >= MAX_SYNC_ATTEMPTS) {
      blocked += 1;
      continue;
    }

    let documentReady: LocalWithdrawalRecord;
    try {
      documentReady = await ensureRequiredDocuments(local);
      await saveRecoverySnapshot(documentReady);
    } catch {
      blocked += 1;
      continue;
    }

    const prepared = await updateRecord(documentReady, {
      server_withdrawal_id: serverItem.id,
      server_withdrawal_number: serverItem.withdrawal_number,
      local_withdrawal_number: serverItem.withdrawal_number ?? local.local_withdrawal_number,
      sync_status: "pending",
      next_sync_attempt_at: null,
      last_error: null,
    });

    try {
      await syncSingleWithdrawal(prepared);
      recovered += 1;
    } catch {
      // syncSingleWithdrawal speichert den konkreten Fehler und den nächsten Retry selbst.
    }
  }

  return { recovered, blocked };
}


export async function forceRecoveryByUuid(clientUuid: string): Promise<void> {
  const currentUser = getLastKnownAuthUser();
  if (!currentUser) throw new Error("Keine aktive Benutzersitzung.");

  const [allLocal, recoverySnapshots] = await Promise.all([
    getAllLocalWithdrawals(),
    getAllRecoverySnapshots(),
  ]);
  const primary = allLocal.find((record) => record.client_uuid === clientUuid) ?? null;

  if (primary && primary.owner_user_id === currentUser.id && primary.record_type !== "draft") {
    await retryLocalSyncByUuid(clientUuid);
    return;
  }

  const serverItems = await fetchIncompleteServerSyncs();
  const serverItem = serverItems.find((item) => item.uuid === clientUuid);
  if (!serverItem) throw new Error("Für diesen Vorgang liegt weder eine lokale Arbeitskopie noch eine serverseitig unvollständige Synchronisierung vor.");
  const snapshot = recoverySnapshots.find((record) => record.client_uuid === clientUuid) ?? null;
  let source = snapshot ?? primary;

  if (!source || source.owner_user_id !== currentUser.id) {
    throw new Error("Passende lokale Arbeitskopie nicht gefunden.");
  }
  if (source.record_type === "draft") {
    throw new Error("Entwürfe werden nicht über die Dokument-Recovery verarbeitet.");
  }
  // Eine fehlende PDF ist kein endgültiger Recovery-Blocker, solange die fachlichen
  // Entnahmedaten vollständig vorhanden sind. Pflichtdokumente werden bei Bedarf
  // deterministisch neu erzeugt.
  source = await ensureRequiredDocuments(source);

  // Nur der Hauptspeicher trägt den operativen Sync-Zustand. Die Recovery-Kopie
  // bleibt eine unveränderte Dokument-Schutzkopie und wird nicht als zweite Queue
  // behandelt. Fehlt der Hauptdatensatz, wird er kontrolliert aus der Schutzkopie
  // wiederhergestellt.
  let stateRecord: LocalWithdrawalRecord = primary ?? {
    ...source,
    sync_status: "pending",
    sync_attempt_count: 0,
    last_sync_attempt_at: null,
    next_sync_attempt_at: null,
    last_error: null,
  };

  const errorText = (unknownError: unknown): string => {
    if (unknownError instanceof Error) return unknownError.message;
    if (typeof unknownError === "string") return unknownError;
    try { return JSON.stringify(unknownError); } catch { return String(unknownError); }
  };

  stateRecord = await updateRecord(stateRecord, {
    server_withdrawal_id: serverItem.id,
    server_withdrawal_number: serverItem.withdrawal_number,
    local_withdrawal_number: serverItem.withdrawal_number ?? stateRecord.local_withdrawal_number,
    sync_status: "syncing",
    sync_attempt_count: 1,
    last_sync_attempt_at: new Date().toISOString(),
    next_sync_attempt_at: null,
    last_error: null,
  });

  try {
    const [serverStatus, serverFiles] = await Promise.all([
      getWithdrawalSyncStatus(clientUuid),
      listWithdrawalServerFiles(serverItem.id),
    ]);
    const version = serverItem.current_version;
    const fileTypes = new Set(
      serverFiles.filter((file) => file.version === version).map((file) => file.file_type)
    );

    const pdfPresent = serverItem.pdf_present || Boolean(serverStatus?.pdf_present) || fileTypes.has("pdf");
    const previewPresent = serverItem.preview_present || Boolean(serverStatus?.preview_present) || fileTypes.has("pdf_preview");
    const labelPresent = serverItem.label_present || Boolean(serverStatus?.label_present) || fileTypes.has("label");

    if (!pdfPresent) {
      const pdfBlob = source.pdf_blob;
      const pdfFilename = source.pdf_filename;
      if (!pdfBlob || !pdfFilename) {
        throw new Error("Lokale PDF-Datei konnte für die Synchronisierung nicht bereitgestellt werden.");
      }
      await uploadWithdrawalFileRaw(serverItem.id, "pdf", pdfBlob, pdfFilename);
    }

    if (!previewPresent && source.pdf_preview_blob && source.pdf_preview_filename) {
      try {
        await uploadWithdrawalFileRaw(serverItem.id, "pdf_preview", source.pdf_preview_blob, source.pdf_preview_filename);
      } catch {
        // Die Vorschau ist kein Abschluss-Blocker. Der Server kann sie später neu erzeugen.
      }
    }

    if (!labelPresent && source.label_blob && source.label_filename) {
      await uploadWithdrawalFileRaw(serverItem.id, "label", source.label_blob, source.label_filename);
    }
    if (!fileTypes.has("source") && source.source_blob && source.source_filename) {
      try { await uploadWithdrawalFileRaw(serverItem.id, "source", source.source_blob, source.source_filename); } catch { /* Vision-Original blockiert Recovery nie */ }
    }
    if (!fileTypes.has("detection_source") && source.detection_source_blob && source.detection_source_filename) {
      try { await uploadWithdrawalFileRaw(serverItem.id, "detection_source", source.detection_source_blob, source.detection_source_filename); } catch { /* Vision-Replay-Quelle blockiert Recovery nie */ }
    }
    if (!fileTypes.has("analysis_input") && source.analysis_input_blob && source.analysis_input_filename) {
      try { await uploadWithdrawalFileRaw(serverItem.id, "analysis_input", source.analysis_input_blob, source.analysis_input_filename); } catch { /* Exaktes OpenCV-Analysebild blockiert Recovery nie */ }
    }
    if (!fileTypes.has("rectification_input") && source.rectification_input_blob && source.rectification_input_filename) {
      try { await uploadWithdrawalFileRaw(serverItem.id, "rectification_input", source.rectification_input_blob, source.rectification_input_filename); } catch { /* Browser-Rectification-Input blockiert Recovery nie */ }
    }
    if (!fileTypes.has("ocr_warp") && source.ocr_warp_blob && source.ocr_warp_filename) {
      try { await uploadWithdrawalFileRaw(serverItem.id, "ocr_warp", source.ocr_warp_blob, source.ocr_warp_filename); } catch { /* OCR-Warp blockiert Recovery nie */ }
    }
    if (!fileTypes.has("vision_feedback") && source.vision_feedback_blob && source.vision_feedback_filename) {
      try { await uploadWithdrawalFileRaw(serverItem.id, "vision_feedback", source.vision_feedback_blob, source.vision_feedback_filename); } catch { /* Feedback blockiert Recovery nie */ }
    }
    if (!fileTypes.has("live_training") && source.live_training_blob && source.live_training_filename) {
      try { await uploadWithdrawalFileRaw(serverItem.id, "live_training", source.live_training_blob, source.live_training_filename); } catch { /* LIVE-Lerndaten blockieren Recovery nie */ }
    }
    if (!fileTypes.has("debug") && source.debug_image_blob && source.debug_image_filename) {
      try { await uploadWithdrawalFileRaw(serverItem.id, "debug", source.debug_image_blob, source.debug_image_filename); } catch { /* Diagnose blockiert nie */ }
    }
    if (!fileTypes.has("debug_data") && source.debug_data_blob && source.debug_data_filename) {
      try { await uploadWithdrawalFileRaw(serverItem.id, "debug_data", source.debug_data_blob, source.debug_data_filename); } catch { /* Diagnose blockiert nie */ }
    }

    const completion = await completeWithdrawalSync(clientUuid, { diagnosticEmailTest: isDiagnosticEmailTest(source) });
    await updateRecord(stateRecord, {
      sync_status: "synced",
      synced_at: completion.synced_at,
      sync_attempt_count: 0,
      next_sync_attempt_at: null,
      // E-Mail und Sync sind strikt getrennt.
      last_error: null,
    });
    await deleteRecoverySnapshot(clientUuid);
    void verifyServerArchiveAndCompact(clientUuid).catch(() => 0);
    window.dispatchEvent(new Event("materialentnahme:status-refresh"));
  } catch (unknownError) {
    const technical = errorText(unknownError);
    await updateRecord(stateRecord, {
      sync_status: "error",
      sync_attempt_count: 1,
      next_sync_attempt_at: null,
      last_error: `Manuelle Recovery fehlgeschlagen: ${technical}`,
    });
    window.dispatchEvent(new Event("materialentnahme:status-refresh"));
    throw new Error(`Manuelle Recovery fehlgeschlagen: ${technical}`, { cause: unknownError });
  }
}


export async function processSyncQueue():
  Promise<void> {
  if (syncRunning) {
    return;
  }

  syncRunning = true;

  try {
    const records =
      await getPendingLocalWithdrawals();

    const currentUser = getLastKnownAuthUser();
    // Browser können bei gestopptem Backend weiterhin navigator.onLine=true
    // melden. In diesem Zustand bleibt die Queue unangetastet und verbraucht
    // keine 1/9-bis-9/9-Versuche.
    if ((await backendAvailabilityForSync()) !== "ok") return;
    const now = Date.now();
    // 9/9 beendet nur die automatischen Sync-Versuche. Der separate Fehlerreport
    // wird weiterhin bei jedem Queue-Lauf (10 s) und beim Online-Event nachgereicht.
    await reportExhaustedSyncFailures(records);
    const processed = new Set<string>();
    for (const record of records) {
      if (!currentUser || record.owner_user_id !== currentUser.id) continue;

      // Ein aktiver Diagnose-Test wird ausschließlich vom Simulations-Sheet gesteuert.
      // Die normale Queue und die Server-Recovery dürfen ihn nicht parallel erfolgreich
      // abschließen und damit einen gewünschten 9/9-Endzustand überschreiben.
      if (isActiveDiagnosticSimulationForUuid(record.client_uuid)) {
        processed.add(record.client_uuid);
        continue;
      }

      const attempts = record.sync_attempt_count ?? 0;
      if (attempts >= MAX_SYNC_ATTEMPTS && !record.deleted_at) continue;
      if (record.last_error?.startsWith("Lokaler Datenfehler ·")) continue;

      const nextAttemptMs = record.next_sync_attempt_at
        ? Date.parse(record.next_sync_attempt_at)
        : Number.NaN;
      if (Number.isFinite(nextAttemptMs) && nextAttemptMs > now) continue;

      processed.add(record.client_uuid);
      // "syncing" ohne aktiven Promise ist nach Reload/Browser-Abbruch ein
      // verwaister Zustand. Durch die erneute Aufnahme kann die UUID-basierte
      // Recovery den bereits serverseitig angenommenen Vorgang fortsetzen.
      try {
        if (record.record_type === "draft") await syncLocalDraft(record);
        else await syncSingleWithdrawal(record);
      } catch {
        /* Ein einzelner Fehler stoppt nicht die gesamte Queue. */
      }
    }

    // Zweite Recovery-Quelle: Der Server kann einen Vorgang bereits kennen,
    // obwohl die lokale Queue ihn versehentlich als erledigt markiert hat oder
    // sein lokaler Status nicht mehr in pending/error/syncing steht. Der Abgleich
    // erfolgt über die stabile UUID und setzt vorhandene lokale Dokumente fort.
    if (currentUser && navigator.onLine) {
      try { await recoverIncompleteServerSyncs(processed); } catch { /* nächster Queue-Lauf versucht erneut */ }
    }
  } finally {
    syncRunning = false;
  }
}